import { Skeleton } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TemplateDropzone } from './TemplateDropzone';
import { TemplateFileStep } from './TemplateFileStep';
import { TemplatePreview } from './TemplatePreview';
import { ContentPanel, usePanel } from 'src/Common/Components/ContentControl/ContentPanel';
import NamedCoordinateDescription from 'src/Common/Components/Stepper/NameCoordinateDescription';
import { LoadingMask } from 'src/Common/Components/LoadingMask';
import { StyledFormHeader } from 'src/Common/Components/Forms/StyledFormHeader';
import TemplateCreateOverview from './TemplateCreateOverview';
import { CenteredColumn, ReactivePanelColumn } from 'src/Common/Components/Stacks';
import {
  TemplateType,
  type TemplateSavePayload,
  type ApplicableRenderersResult,
  type RendererPayload,
  type RendererResult,
  type Template,
} from '@ekkogmbh/apisdk';
import useRequest from 'src/Common/Hooks/useRequest';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { CoordinateInput } from 'src/Common/Components/CoordinateInput';
import { enumKeys } from 'src/Common/Helper/Enum';
import { StyledSelectField } from 'src/Common/Components/Forms/StyledSelectField';
import useEkko from 'src/Common/Providers/EkkoProvider';
import { isBase64 } from 'src/Common/formatSupport';

interface Props {
  closeHandler: () => void;
  saveHandler: (name: string, coordinate: string, payload: TemplateSavePayload) => Promise<void>;
  /** @todo maybe build a "renderer context" that provides these functions */
  renderTemplate: (payload: RendererPayload, onAccept?: (result: RendererResult) => void) => void;
  template?: Template;
  isRendering: boolean;
}

export const TemplatePanel = ({
  template,
  isRendering,
  saveHandler,
  closeHandler,
  renderTemplate,
}: Props): React.JSX.Element => {
  const { api } = useEkko();
  const { currentStep, enableNext, enableSave } = usePanel();
  const [isProcessing, startRequest] = useRequest();

  const [name, setName] = useState<string>('');
  const [coordinate, setCoordinate] = useState<string>('');
  const [data, setData] = useState<string>('');
  const [type, setType] = useState<TemplateType>(TemplateType.APACHE_FOP);
  const [errors, setErrors] = useState<string[]>([]);
  const [file, setFile] = useState<File>();
  const [rendering, setRendering] = useState<RendererResult>();

  const isAllFilled = ![name, coordinate, data].includes('') && type !== undefined;

  /** Get state from template entity */
  const reset = useCallback(() => {
    setName(template?.name ?? '');
    setCoordinate(template?.coordinate ?? '');
    setData(template?.data ?? '');
    setFile(undefined);
    setRendering(undefined);
  }, [template]);

  /** Conditional panel controls enablement */
  useEffect(() => {
    switch (currentStep) {
      case 0:
        // There are some edge cases where the image field might just contain an error string
        enableNext((rendering !== undefined && isBase64(rendering.image)) || template !== undefined);
        break;
      case 1:
        enableNext(name !== '' && coordinate !== '');
        break;
      case 2:
        enableSave(isAllFilled);
        break;
    }
  }, [currentStep, rendering, template, name, coordinate, isAllFilled]);

  useEffect(() => {
    reset();
  }, [template]);

  /** Deduce template type with given data */
  useEffect(() => {
    if (data === '') {
      setType(TemplateType.APACHE_FOP);
      setErrors([]);
      return;
    }

    startRequest(
      () => api.getApplicableRenderers({ data }),
      (result) => {
        let applicableType: TemplateType | undefined = undefined;
        const errors: string[] = [];
        Object.entries(result as ApplicableRenderersResult).forEach(([name, value]) => {
          if (name === 'identity') {
            // ignore identity renderer, as it is for internal use only.
            return;
          }
          if (value.isApplicable) {
            applicableType = name as TemplateType;
          } else {
            errors.push(`Renderer of type ${name} is not applicable to render the given template: ${value.reason}`);
          }
        });

        setType(applicableType ?? TemplateType.APACHE_FOP);
        setErrors(applicableType === undefined ? errors : []);
      },
    );
  }, [data]);

  /** Render when template data changes */
  useEffect(() => {
    if (data === '' || type === undefined) {
      return;
    }

    renderTemplate(
      {
        data,
        type,
        fields: {},
        annotations: {},
        autoFill: true,
        render: true,
      },
      (result) => {
        setErrors(result.errors);
        setRendering(result);
      },
    );
  }, [data, type]);

  const handleSave = useCallback(() => {
    if (isAllFilled) {
      saveHandler(name, coordinate, { data, type, metadata: {} });
    }
  }, [saveHandler, name, coordinate, data, type]);

  const preview = useMemo(() => {
    if (isRendering) {
      return <Skeleton variant={'rectangular'} animation={'pulse'} height={200} />;
    }

    if (rendering === undefined) {
      return <></>;
    }

    return (
      <CenteredColumn width={'100%'}>
        <StyledFormHeader label={'render preview'} />
        <TemplatePreview extended={errors.length != 0} preRender={rendering} />
      </CenteredColumn>
    );
  }, [template, rendering, isRendering]);

  return (
    <ContentPanel
      onSave={handleSave}
      onReset={reset}
      onCancel={closeHandler}
      steps={[
        {
          title: 'Template',
          description: <NamedCoordinateDescription name={name} coordinate={coordinate} />,
          contentCallback: () => (
            <ReactivePanelColumn>
              {isProcessing && <LoadingMask />}
              {file ? (
                <TemplateFileStep file={file} />
              ) : (
                <TemplateDropzone dataCallback={(d) => setData(d)} fileCallback={(f) => setFile(f)} />
              )}
              {preview}
            </ReactivePanelColumn>
          ),
        },
        {
          title: 'Identifier',
          contentCallback: () => (
            <ReactivePanelColumn>
              <StyledTextField
                label={'Name'}
                value={name}
                onChange={(e) => {
                  setName(String(e.target.value).trim());
                }}
                disabled={template !== undefined}
              />
              <CoordinateInput
                value={coordinate}
                onChange={(value) => {
                  setCoordinate(String(value).trim());
                }}
                disabled={template !== undefined}
                trailingDelimiter={false}
              />
              <StyledSelectField native disabled value={type} onChange={undefined} label={'Type'}>
                {enumKeys(TemplateType).map((possibleTemplateType) => (
                  <option key={possibleTemplateType} value={TemplateType[possibleTemplateType]}>
                    {possibleTemplateType}
                  </option>
                ))}
              </StyledSelectField>
            </ReactivePanelColumn>
          ),
        },
        {
          title: 'Overview',
          contentCallback: () => (
            <TemplateCreateOverview template={{ type, name, coordinate }} file={file} rendererResult={rendering} />
          ),
        },
      ]}
    />
  );
};
